/**
 * Public environment variables exposed to the client via Vite's `import.meta.env`.
 * All variables MUST use the `VITE_` prefix to be included in the client bundle.
 *
 * Renamed from `env.client.ts` to follow TanStack Start import-protection conventions.
 * The `.client.*` suffix is reserved for client-only modules that should be excluded
 * from SSR bundles — this file is safe for SSR (contains no browser APIs at module scope).
 */

import { Schema } from "effect";

import {
  normalizeOptionalEnv,
  OptionalBooleanString,
  parseBooleanEnv,
  SentryDsn,
  Url,
} from "@vivus/common";

const BrowserSafeExportUrl = Schema.String.pipe(
  Schema.check(
    Schema.makeFilter(
      (value) => {
        if (value.startsWith("/")) {
          return !value.startsWith("//");
        }

        try {
          const parsed = new URL(value);

          return (
            parsed.protocol === "http:" ||
            parsed.protocol === "https:"
          );
        } catch {
          return false;
        }
      },
      {
        message:
          "Expected an absolute http(s) URL or root-relative path",
      }
    )
  )
);

const ClientEnvSchema = Schema.Struct({
  VITE_BASE_URL: Url,
  VITE_BETTER_AUTH_URL: Url,
  VITE_ENVIRONMENT: Schema.String,
  VITE_OTEL_BROWSER_EXPORT_URL: Schema.optional(
    BrowserSafeExportUrl
  ),
  VITE_OTEL_TRACES_ENABLED: Schema.optional(
    OptionalBooleanString
  ),
  VITE_SENTRY_DSN: Schema.optional(SentryDsn),
  VITE_VERSION: Schema.String,
  VITE_WAITLIST_MODE: Schema.optional(Schema.String),
});

const parsed = Schema.decodeUnknownSync(ClientEnvSchema)({
  ...import.meta.env,
  VITE_OTEL_BROWSER_EXPORT_URL: normalizeOptionalEnv(
    import.meta.env.VITE_OTEL_BROWSER_EXPORT_URL
  ),
});

export const env = {
  ...parsed,
  VITE_OTEL_TRACES_ENABLED: parseBooleanEnv(
    true,
    parsed.VITE_OTEL_TRACES_ENABLED
  ),
  VITE_WAITLIST_MODE:
    (parsed.VITE_WAITLIST_MODE ?? "false") === "true",
};
